import _ from "lodash";
import { getOrganizationRecipient } from "../../../services/documentRecipientService";
import { getResponsibleInstitutes } from "../../../services/Product/ProductService/productService";
import { getExternalReference } from "../CasePdfGenerator/casePdfGeneratorService";
import { MediaObject } from "../../../types/MediaObject";
import { isValidUploadableFile } from "../../../services/mediaObjectService";
import { Person } from "../../../types/Person";
import { ProductUnion } from "../../../types/ProductUnion";
export const hasFaxOrBea = (product: ProductUnion, path: string): boolean => {
  return !!(_.get(product, `${path}.fax`) || _.get(product, `${path}.beaSafeId`));
};

export const getEntityPathByRecipientType = (product: ProductUnion, recipientType: string): string => {
  const { courtPath, authorityPath, prosecutionPath } = getResponsibleInstitutes(product);
  switch (true) {
    case recipientType === "court":
      return courtPath;
    case recipientType === "authority":
      return authorityPath;
    case recipientType === "prosecution":
      return prosecutionPath;
    case recipientType === "debtCollectionAgency":
    case recipientType === "contractualPartnerLegalEntity":
      return `${recipientType}.organization`;
    case recipientType === "opposingCounsel":
      return "opponentLawFirm.organization";
    case recipientType === "propertyManager":
      return "propertyManager.organization";
    case recipientType.startsWith("processParticipantsPeople"):
      const index = product.processParticipantsPeople.findIndex(
        (person: Person) => person.id + "" === recipientType.split(".")[1]
      );
      return "processParticipantsPeople[" + index + "]";
    case recipientType === "custom":
    default:
      return "";
  }
};

export const getBeaSafeId = (product: ProductUnion, recipientType: string): string => {
  if (recipientType.startsWith("processParticipantsOrganizations")) {
    return getOrganizationRecipient(product, recipientType)?.organization.beaSafeId ?? "";
  }
  const entityPath = getEntityPathByRecipientType(product, recipientType);
  return _.get(product, `${entityPath}.beaSafeId`, "");
};

export const getFaxNumber = (product: ProductUnion, recipientType: string): string => {
  if (recipientType.startsWith("processParticipantsOrganizations")) {
    return getOrganizationRecipient(product, recipientType)?.organization.fax ?? "";
  }
  const entityPath = getEntityPathByRecipientType(product, recipientType);
  return _.get(product, `${entityPath}.fax`, "");
};

export const getCommunicationMedium = (product: ProductUnion, recipientType: string): string => {
  if (!product?.backofficeCase?.lawyer?.person?.beaData) {
    return "fax";
  }

  if (recipientType.startsWith("processParticipantsOrganizations")) {
    return getBeaSafeId(product, recipientType) ? "bea" : "fax";
  }
  const entityPath = getEntityPathByRecipientType(product, recipientType);
  if (entityPath && !getBeaSafeId(product, recipientType)) {
    return "fax";
  }

  if (recipientType === "authority" && _.get(product, entityPath + ".communicationMedium")) {
    const { authorityPath } = getResponsibleInstitutes(product);
    return _.get(product, authorityPath + ".communicationMedium", "fax");
  }

  if (
    _.includes(["court", "authority", "prosecution", "opposingCounsel", "custom"], recipientType) ||
    recipientType.startsWith("processParticipantsPeople")
  ) {
    return "bea";
  }
  return "fax";
};

export const handleReferenceLogic = (product: ProductUnion, recipientType: string) => {
  switch (true) {
    case recipientType === "contractualPartnerLegalEntity":
    case _.startsWith(recipientType, "processParticipantsOrganizations"):
      return {
        externalReference: "",
        externalReferenceIsEditable: false,
      };
    case recipientType === "custom":
      return {
        externalReference: "",
        externalReferenceIsEditable: true,
      };
    default:
      const externalReference = getExternalReference(product, recipientType);
      return {
        externalReference: externalReference || "",
        externalReferenceIsEditable: !externalReference,
      };
  }
};

const FILE_SIZE_LIMIT = 20000000;
const ALLOWED_BEA_MIME_TYPES = new Set([
  "application/pdf",
  "image/jpeg",
  "image/png",
  "video/mp4",
  "video/avi",
  "video/mpeg",
  "video/webm",
  "audio/mp3",
  "audio/ogg",
  "audio/mpeg",
]);
export const isValidBeaMediaObject = (mediaObject: MediaObject): boolean => {
  if (mediaObject.size > FILE_SIZE_LIMIT || !isValidUploadableFile(mediaObject.originalName, false)) {
    return false;
  }

  if (ALLOWED_BEA_MIME_TYPES.has(mediaObject.mimeType)) {
    return true;
  }

  // special mahnverfahren files
  return (
    (mediaObject.mimeType === "text/plain" && _.endsWith(mediaObject.originalName, ".eda"))
  );
};

export const getSubject = (product: ProductUnion): string => {
  if (["contractLaw", "administrativeLaw"].includes(product.productCategory)) {
    return product.vsString;
  }

  switch (product.productClassName) {
    case "divorce":
      return `Familiensache ${product.client?.fullName} ./. ${product.partner?.fullName}`;
    case "traffic":
      return `Ordnungswidrigkeitsverfahren ${product.client?.fullName}`;
    case "settlement":
      return `${product.client?.familyName} ./. ${product.employerOrganization?.name}`;
    case "rentalContract":
    case "trafficAccident":
    case "employmentContract":
    case "generalCivilLaw":
      return product.vsString ?? "";
    default:
      return "";
  }
};
